import { ExternalLink, Star, Quote, Award, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";

const AMAZON_URL = "https://www.amazon.com/dp/B0GDQYKC6J?ref=cm_sw_r_ffobk_cp_ud_dp_B6F4FFWY65X375E1Q77R&ref_=cm_sw_r_ffobk_cp_ud_dp_B6F4FFWY65X375E1Q77R&social_share=cm_sw_r_ffobk_cp_ud_dp_B6F4FFWY65X375E1Q77R&bestFormat=true";

const COVER_PLACEHOLDER =
  "https://customer-assets.emergentagent.com/job_property-mentor-4/artifacts/krc05pij_Screenshot%202026-06-03%20at%2010.39.32.png";

const praise = [];

const chapters = [
  "The Story You Inherited",
  "Naming What You Don't Want to Repeat",
  "The Quiet Work of Forgiveness",
  "Rituals That Hold a Family Together",
  "Raising Kids Who Tell the Truth",
  "Writing a New Ending",
];

export default function TheBook() {
  return (
    <div data-testid="the-book-page">
      {/* Hero */}
      <section className="bg-muted/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-24 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="relative max-w-xs mx-auto md:mx-0">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/25 to-secondary/30 rounded-2xl rotate-[-3deg]" />
              <img
                src={COVER_PLACEHOLDER}
                alt="Rewrite Your Family Story — Tammy Richard"
                className="relative rounded-2xl shadow-xl w-full aspect-[3/4] object-cover"
                data-testid="book-cover"
              />
              <div className="absolute -bottom-4 -right-4 bg-card border border-border rounded-full px-4 py-2 text-xs uppercase tracking-[0.22em] text-primary">
                New Release
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            <span className="text-[11px] uppercase tracking-[0.3em] text-primary">
              The Book
            </span>
            <h1
              className="font-serif text-5xl md:text-6xl lg:text-7xl mt-4 leading-[1.02]"
              data-testid="book-title"
            >
              Rewriting the
              <br />
              <span className="italic text-primary">Family Story.</span>
            </h1>
            <p className="text-foreground/70 text-sm mt-3 max-w-md">
              A Guide to Breaking Generational Cycles
            </p>
            <p className="text-foreground/55 text-sm mt-1">by Tammy J. Richard</p>

            <div className="flex items-center gap-2 mt-6 text-foreground/70 text-sm">
              <Star size={14} className="text-primary" fill="currentColor" strokeWidth={0} />
              <span>Available now on Amazon</span>
            </div>

            <p className="text-lg text-foreground/85 mt-8 leading-relaxed max-w-xl">
              A tender, practical guide to breaking the cycles you inherited
              — and building a family culture you're proud of. Part memoir,
              part workbook, Tammy walks readers through the quiet work of
              rewriting what gets passed down.
            </p>

            <div className="flex flex-wrap gap-3 mt-10">
              <a
                href={AMAZON_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-amazon-cta"
              >
                <Button className="rounded-full bg-primary hover:bg-primary/90 btn-lift h-12 px-8 text-base">
                  Buy on Amazon <ExternalLink className="ml-2" size={16} />
                </Button>
              </a>
              <a
                href={AMAZON_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-kindle-cta"
              >
                <Button variant="outline" className="rounded-full h-12 px-8 border-border">
                  Kindle / Paperback
                </Button>
              </a>
            </div>

            <p className="text-xs text-foreground/55 mt-4">
              Available in Kindle, paperback, and audiobook formats.
            </p>
          </div>
        </div>
      </section>

      {/* As Featured on Amazon */}
      <section className="bg-card border-y border-border" data-testid="amazon-feature-strip">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-primary">
              <Award size={14} /> As Featured On
            </div>
            <p className="font-serif text-3xl md:text-4xl mt-3 leading-tight">
              Available on <span className="text-[#FF9900]">amazon</span>.
            </p>
            <p className="text-foreground/70 mt-4 text-sm leading-relaxed max-w-md">
              "Rewriting the Family Story" is published on Amazon in Kindle,
              paperback, and audiobook formats.
            </p>
            <a
              href={AMAZON_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="amazon-feature-cta"
              className="inline-flex items-center gap-2 mt-6 text-sm font-medium text-primary hover:gap-3 transition-all"
            >
              See it on Amazon <ExternalLink size={14} />
            </a>
          </div>

          <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-4">
            <BadgeCard
              icon={<span className="font-bold text-[#FF9900] text-2xl tracking-tight">a<span className="lowercase">mazon</span></span>}
              label="Published"
              value="Worldwide"
            />
            <BadgeCard
              icon={<BookOpen className="text-primary" size={22} strokeWidth={1.6} />}
              label="Formats"
              value="Kindle · Print · Audio"
            />
            <BadgeCard
              icon={<Award className="text-primary" size={22} strokeWidth={1.6} />}
              label="Author"
              value="Tammy J. Richard"
            />
          </div>
        </div>
      </section>

      {/* What's inside */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <h2 className="font-serif text-4xl md:text-5xl leading-tight">
            What's inside.
          </h2>
          <p className="mt-6 text-foreground/75 leading-relaxed">
            Six chapters of stories, frameworks, and reflective prompts. Read
            it in a weekend, or move through one chapter a month with your
            partner or a small group.
          </p>
        </div>
        <div className="md:col-span-6 md:col-start-7">
          <ol className="space-y-4">
            {chapters.map((c, i) => (
              <li key={c} className="flex gap-5 items-start border-b border-border pb-4">
                <span className="font-serif text-3xl text-primary leading-none mt-1">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-serif text-xl text-foreground leading-snug">{c}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Praise */}
      {praise.length > 0 && (
      <section className="bg-muted/40 py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-12">
            <Quote className="text-primary" size={28} strokeWidth={1.4} />
            <h2 className="font-serif text-4xl md:text-5xl">Reader praise.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {praise.map((p, i) => (
              <blockquote
                key={p.by}
                className="bg-card border border-border rounded-2xl p-8"
                data-testid={`book-praise-${i}`}
              >
                <p className="font-serif italic text-xl leading-snug">"{p.quote}"</p>
                <p className="text-xs uppercase tracking-[0.22em] text-foreground/60 mt-6">
                  — {p.by}
                </p>
              </blockquote>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Final CTA */}
      <section className="max-w-3xl mx-auto px-6 py-28 text-center">
        <h2 className="font-serif text-4xl md:text-5xl leading-tight">
          Take it home.
        </h2>
        <p className="text-lg text-foreground/75 mt-6">
          Pick up your copy on Amazon — and consider getting a second one for
          someone who needs it.
        </p>
        <a
          href={AMAZON_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="book-bottom-cta"
        >
          <Button className="mt-10 rounded-full bg-primary hover:bg-primary/90 btn-lift h-12 px-8 text-base">
            Buy on Amazon <ExternalLink className="ml-2" size={16} />
          </Button>
        </a>
      </section>
    </div>
  );
}

function BadgeCard({ icon, label, value }) {
  return (
    <div className="bg-background border border-border rounded-xl p-5 flex flex-col items-start gap-2">
      <div className="h-7 flex items-center">{icon}</div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-foreground/55">{label}</p>
      <p className="font-serif text-lg leading-tight">{value}</p>
    </div>
  );
}
